import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { PageFrame } from '@/components/layout/PageFrame'
import { PAGE_TITLE_CLASS } from '@/components/layout/PageHeader'
import { useTaskView } from '@/components/custom/CustomSlots'
import { ProposalSummary } from '@/components/process/ProposalSummary'
import { ReopenTaskDialog } from '@/components/process/ReopenTaskDialog'
import { REOPENABLE_KINDS } from '@/components/process/stepLive'
import { dueLine } from '@/components/process/stepLiveText'
import { oneFurther, taskLinkState, taskPath } from '@/components/process/taskLink'
import { TaskForm } from '@/components/process/TaskForm'
import { TaskStatusBlock } from '@/components/process/TaskStatusBlock'
import { LIVE_TASK_STATUSES } from '@/components/process/taskStatusText'
import { SourceLink } from '@/components/records/SourceLink'
import { REVIEW_IN_INTAKE, REVIEW_IN_QUOTES, responseBannerText, responseHref, updateHref } from '@/components/records/customerUpdateText'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { useWorkflowDefinition } from '@/hooks/useWorkflowDefinition'
import { useRecordUpdateNoticeStore } from '@/stores/recordUpdateNoticeStore'
import { Badge } from '@/components/ui'
import { useOptionalToast } from '@/components/ui/Toast'
import { processErrorMessage, processKey, useClaimTask, useCompleteTask, useProcessTask, useRecordProcess, useReopenTask, useSaveTaskForm } from '@/hooks/useProcess'
import type { TaskRead } from '@/types/process'
import type { ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { projectStepDetail } from '@/utils/workflowOverview'
import { AiToldDisclosure } from './AiToldDisclosure'
import { holdBackLanding } from './backLanding'
import { landingPath, pageWidth, readOrigin, recordOrigin } from './taskPageModel'
import { BREADCRUMB, doneToast, recordOriginLabel, stepCrumb } from './taskPageText'
import { tourAnchor } from '@/components/tour/tourAnchor'

// E5 (spec §6.3): a person's completed task can be re-opened. The outbox
// kinds (automation / ai) and waits never offer it here; the API is the
// authority either way (403 / 409 are worded by `processErrorMessage`).
// SA-460: one set, shared with the drawer's live half (`stepLive`).
// SA-481: offered only when the server says this viewer may (`TaskRead.can_reopen`).
// SA-1032: only an open or claimed task shows its form; every other status
// says what ended it and where the process is now (`TaskStatusBlock`).
// SA-1078 (UX pass spec move 3, Q4): the frame is as wide as the content
// needs (`pageWidth`), the page goes back to where it was opened from
// (`readOrigin` over router state, the record as the fallback) through a
// breadcrumb and after a completion, "What the AI is told" is a closed
// disclosure under the form, and the due time names its zone once.

export function TaskDetailPage() {
  const { taskId } = useParams<{ taskId: string }>()
  const task = useProcessTask(taskId)
  if (task.isError) {
    return (
      <PageFrame width="form" className="space-y-2">
        <p className="text-sm text-destructive">{processErrorMessage(task.error)}</p>
        <Link to="/tasks" className="text-xs underline">Back to tasks</Link>
      </PageFrame>
    )
  }
  if (task.isLoading || !task.data) return <p className="text-sm text-muted-foreground">Loading…</p>
  return <TaskPageBody key={task.data.id} task={task.data} />
}

/** The loaded page: a component of its own because `useTaskView` is a hook and
 * the task exists only after the loading and error returns (E5 spec §7.2). */
function TaskPageBody({ task: t }: { task: TaskRead }) {
  const claim = useClaimTask(); const save = useSaveTaskForm(); const complete = useCompleteTask()
  const reopen = useReopenTask()
  const [reopening, setReopening] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useOptionalToast()
  const location = useLocation()
  const noun = useRecordNoun()
  const zone = useDefaultTimezone()
  const notice = useRecordUpdateNoticeStore((s) => s.notices[t.order_id])
  const live = LIVE_TASK_STATUSES.has(t.status)
  const View = useTaskView(t)
  const width = pageWidth(t, live && View !== null)
  const origin = readOrigin(location.state, recordOrigin(t.order_id))
  const originLabel = origin.label ?? recordOriginLabel(noun.singular, t.order_number)
  // The AI block: the record read names the workflow, the definition names the
  // step's executor. Asked only while the form shows; a failed or refused read
  // is simply no disclosure (a viewer without `workflows:read`).
  const record = useRecordProcess(live ? t.order_id : undefined)
  const definition = useWorkflowDefinition(live ? record.data?.workflow_id : undefined)
  const def = definition.data?.definition.kind === 'process' ? (definition.data.definition as ProcessWorkflowDefinitionJson) : null
  const ai = def ? projectStepDetail(def, t.step_id)?.ai ?? null : null
  const busy = claim.isPending || save.isPending || complete.isPending
  const Work = View ?? TaskForm
  return (
    <PageFrame width={width} className="space-y-4">
      <nav aria-label={BREADCRUMB} className="text-xs text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1">
          <li><Link to={origin.to} className="hover:underline">{originLabel}</Link></li>
          <li aria-hidden="true">›</li>
          <li aria-current="page">{stepCrumb(t.step_number, t.step_title)}</li>
        </ol>
      </nav>
      <header className="space-y-1" {...tourAnchor('task.header.title')}>
        <h1 className={PAGE_TITLE_CLASS}>{t.step_number} {t.step_title}</h1>
        <p className="text-sm text-muted-foreground">
          <Link to={`/orders?order=${t.order_id}`} className="font-mono underline-offset-2 hover:underline">{t.order_number}</Link>
          {' '}<SourceLink className="align-middle" source={t.source ? { kind: t.source.kind, conversationId: t.source.conversation_id } : null} />
          {t.record_title ? ` · ${t.record_title}` : ''} {t.seat_label && <Badge variant="neutral">{t.seat_label}</Badge>} {t.kind !== 'work' && <Badge variant={t.kind === 'attention' ? 'warning-soft' : 'neutral'}>{t.kind}</Badge>} {t.due_at && <> · due {dueLine(t.due_at, new Date(), zone)}</>} {t.overdue && <Badge variant="destructive">overdue</Badge>}
        </p>
        {t.status === 'open' && t.kind !== 'wait' && <button type="button" className="text-xs underline" disabled={busy} onClick={() => claim.mutate({ taskId: t.id })} {...tourAnchor('task.header.claim')}>Claim</button>}
      </header>
      {notice && (
        <p role="status" data-testid="task-update-banner" className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-sm">
          {notice.kind === 'response' ? responseBannerText(notice.orderNumber || t.order_number) : `A customer update arrived on ${notice.orderNumber || t.order_number}`} ·{' '}
          <Link to={notice.kind === 'response' ? responseHref(t.order_id) : updateHref(t.order_id)} className="font-medium text-primary underline-offset-2 hover:underline">
            {notice.kind === 'response' ? REVIEW_IN_QUOTES : REVIEW_IN_INTAKE}
          </Link>
        </p>
      )}
      {live ? (
        <>
          <Work task={t} busy={busy}
            onSaveDraft={(fields) => save.mutate({ taskId: t.id, fields })}
            onComplete={(exit, fields) => complete.mutate({ taskId: t.id, exit, fields }, {
              onSuccess: (record) => {
                // SA-1328: the record page reads the answer at once and the toast says
                // what happened. SA-1338: a task opened from its own record goes back to
                // the record's own entry, which the Quotes page replaces with the landing
                // (`useBackLanding`), so one Back leaves the record; without `back` (a
                // shared link, a reload) the task page replaces itself with the landing.
                queryClient.setQueryData([...processKey, 'record', t.order_id], record)
                toast?.addToast({ type: 'success', title: doneToast(t, exit, record) })
                const land = landingPath(origin.to, t.order_id)
                if (land && origin.back) {
                  holdBackLanding(t.order_id)
                  navigate(-origin.back)
                } else if (land) navigate(land, { replace: true })
                else navigate(origin.to)
              },
            })} />
          {ai && <AiToldDisclosure ai={ai} />}
        </>
      ) : (
        <div className="space-y-2">
          <TaskStatusBlock task={t} />
          {t.status === 'done' && t.proposal && <ProposalSummary proposal={t.proposal} />}
          {t.status === 'done' && t.can_reopen && REOPENABLE_KINDS.has(t.kind) && (
            <button type="button" className="text-xs underline" onClick={() => { reopen.reset(); setReopening(true) }} {...tourAnchor('task.done.reopen')}>
              Re-open
            </button>
          )}
          {reopening && (
            <ReopenTaskDialog stepTitle={t.step_title} busy={reopen.isPending}
              error={reopen.error ? processErrorMessage(reopen.error) : null}
              onClose={() => setReopening(false)}
              onConfirm={(reason) => reopen.mutate({ taskId: t.id, reason }, {
                onSuccess: (recordRead) => {
                  // The step's new task when the viewer can open it (SA-480), carrying
                  // this page's origin forward (SA-1078); else the record.
                  const next = recordRead.open_tasks.find((o) => o.step_id === t.step_id && o.can_open)
                  if (next) navigate(taskPath(next.id), { state: taskLinkState(oneFurther(origin)) })
                  else navigate(`/orders?order=${t.order_id}`)
                },
              })} />
          )}
        </div>
      )}
      {claim.error && <p className="text-sm text-destructive">{processErrorMessage(claim.error)}</p>}
      {save.error && <p className="text-sm text-destructive">{processErrorMessage(save.error)}</p>}
      {complete.error && <p className="text-sm text-destructive">{processErrorMessage(complete.error)}</p>}
    </PageFrame>
  )
}
